import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { brokers, members, type BrokerOnboardingResult } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDocument } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { CopyButton } from '@/shared/ui/CopyButton';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton } from '@/shared/ui/Skeleton';
import { buttonClassName } from '@/shared/ui/buttonStyles';

import { canaisDoAssistente } from './assistenteDoCorretor';
import { BrokerWizard, type PerfilDoAssistente } from './BrokerWizard';
import { permissaoDadosBancarios } from './contaBancaria';

/**
 * "Cadastrar novo corretor" do OnPoint (`/corretores/cadastrar-novo-corretor`), pela
 * seguradora: o assistente de quatro passos e, no fim, os convites emitidos.
 */
export function BrokerOnboardingPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [resultado, setResultado] = useState<BrokerOnboardingResult | null>(null);

  const podeCadastrar = can('corretores.corretor.create');
  // Convidar concede perfil: as mesmas permissoes do convite com corretora.
  const podeConvidar = can('usuarios.usuario.create') && can('usuarios.papel.update');
  const podeAlterarConta = can(permissaoDadosBancarios);

  const canais = useQuery({
    queryKey: ['broker-channel-types'],
    queryFn: ({ signal }) => brokers.channelTypes({}, signal),
    enabled: podeCadastrar,
  });
  const perfis = useQuery({
    queryKey: ['members', 'grantable-roles'],
    queryFn: ({ signal }) => members.grantableRoles(signal),
    enabled: podeCadastrar && podeConvidar,
  });

  if (!podeCadastrar) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Cadastrar novo corretor" />
        <Alert tone="warning" title="Sem permissao">
          O cadastro de corretor pede a permissao de cadastrar corretor.
        </Alert>
      </div>
    );
  }

  // So os perfis da corretora: a pessoa da corretora nova nao recebe perfil interno.
  const perfisDaCorretora: PerfilDoAssistente[] = (perfis.data ?? [])
    .filter((perfil) => perfil.broker_scoped && perfil.is_assignable !== false)
    .map((perfil) => ({
      id: perfil.id,
      name: perfil.name,
      key: perfil.key,
      is_default: perfil.is_default,
    }));

  const carregando = canais.isPending || (podeConvidar && perfis.isPending);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Cadastrar novo corretor"
        description="O corretor nasce na primeira etapa da esteira de ativacao, com o administrador do portal e os usuarios convidados."
        actions={
          <Link
            to="/corretores"
            className={buttonClassName({ variant: 'secondary', size: 'md' })}
          >
            Lista de Corretores
          </Link>
        }
      />

      {canais.isError ? (
        <Alert tone="warning" title="Nao foi possivel carregar os canais">
          {canais.error.message}
        </Alert>
      ) : null}
      {perfis.isError ? (
        <Alert tone="warning" title="Nao foi possivel carregar os perfis da corretora">
          {perfis.error.message}
        </Alert>
      ) : null}

      {resultado ? (
        <CadastroConcluido resultado={resultado} />
      ) : carregando ? (
        <Card>
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-10 w-full" />
          </div>
        </Card>
      ) : (
        <BrokerWizard
          canais={canaisDoAssistente(canais.data ?? [])}
          perfis={perfisDaCorretora}
          podeAlterarConta={podeAlterarConta}
          podeConvidar={podeConvidar}
          verificarCnpj={(cnpj) => brokers.registrationCheck(cnpj)}
          concluir={(pedido) => brokers.onboard(pedido)}
          onConcluido={(feito) => {
            setResultado(feito);
            void queryClient.invalidateQueries({ queryKey: ['brokers'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            void queryClient.invalidateQueries({ queryKey: ['invitations'] });
          }}
          onCancelar={() => void navigate('/corretores')}
          caminhoDaCorretora={(id) => `/corretores/${id}`}
        />
      )}
    </div>
  );
}

/**
 * O "Seu cadastro foi concluido com sucesso" do OnPoint.
 *
 * O OnPoint diz que a pessoa recebera um e-mail com as instrucoes de acesso. Sem
 * provedor de e-mail conectado (EMI-002, regra pendente), o SeguraCore mostra o
 * endereco de aceite de cada convite, uma vez, para quem cadastrou entregar.
 */
function CadastroConcluido({ resultado }: { resultado: BrokerOnboardingResult }) {
  const { broker, invitations } = resultado;
  const aEntregar = invitations.filter((convite) => !convite.delivered_by_email);
  return (
    <Card>
      <div className="flex flex-col gap-4 p-5">
        <Alert tone="success" title="Seu cadastro foi concluido com sucesso.">
          {broker.legal_name}, CNPJ {formatDocument(broker.document)}, numero{' '}
          {broker.number}. A corretora esta na etapa Novo da esteira de ativacao.
        </Alert>

        {invitations.length === 0 ? (
          <p className="text-sm text-ink-600">
            Nenhuma pessoa foi convidada. Os convites da corretora saem pela tela de
            usuarios.
          </p>
        ) : (
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-ink-900">Convites emitidos</h2>
            {aEntregar.length > 0 ? (
              <p className="text-xs text-ink-600">
                O envio automatico de e-mail ainda nao esta conectado. Envie a cada
                pessoa o endereco de aceite abaixo: ele aparece so agora.
              </p>
            ) : null}
            <ul className="flex flex-col gap-2">
              {invitations.map((convite) => (
                <li
                  key={convite.id}
                  className="flex flex-col gap-1 rounded-md border border-line p-3 text-sm"
                >
                  <span className="font-medium text-ink-900">
                    {convite.name} ({convite.email})
                    {convite.portal_admin ? ' - administrador do portal' : ''}
                  </span>
                  {convite.delivered_by_email ? (
                    <span className="text-xs text-ink-500">
                      Convite enviado por e-mail.
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 break-all text-xs text-ink-600">
                      {convite.accept_url}
                      <CopyButton
                        value={convite.accept_url}
                        label={`Copiar endereco de aceite de ${convite.email}`}
                      />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Link
            to="/corretores"
            className={buttonClassName({ variant: 'secondary', size: 'md' })}
          >
            Voltar para a lista
          </Link>
          <Link
            to={`/corretores/${broker.id}`}
            className="inline-flex h-10 items-center rounded-md bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600"
          >
            Abrir cadastro
          </Link>
        </div>
      </div>
    </Card>
  );
}
